<!-- src/components/SmsThreadPanel.vue -->
<!--
  Presentational SMS thread panel for embedding inside per-record views
  (currently VoiceMailRecord, but reusable elsewhere). Lives "between
  the header and the body" of the parent record.

  Pure props in / events out — knows nothing about axios. The parent
  owns the data (thread array, consent state) and handles network calls.

  Layout: chevron header (always visible) -> body when unfurled, with
  scrollable thread + composer. Max body height ~300px so it doesn't
  overwhelm the surrounding record.

  Composer behavior is consent-state aware:
    pending  -> disabled hint
    granted  -> full composer with attach + send
    declined -> closed informational message
    revoked  -> closed informational message
    unknown  -> manual "Send consent prompt" button (dev/fallback)

  Inbound messages get auto-acknowledged when the panel is unfurled
  (single 'acknowledge' emit; parent handles the network side).
-->

<template>
  <div class="sms-thread-panel" :class="`chrome-${chrome}`">
    <!-- Header (only when chrome=full) -->
    <div v-if="chrome === 'full'" class="panel-header" @click="toggleUnfurl">
      <span class="chevron">{{ isUnfurled ? '▾' : '▸' }}</span>
      <span class="label">Text messaging</span>
      <span
        v-if="requiresConsent"
        :class="['status-pill', `status-${consent.status}`]"
      >
        {{ statusText }}
      </span>
      <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
    </div>

    <!-- Body. When chrome=full, hidden until unfurled. When chrome=bare,
         always shown (parent provides the unfurl affordance). -->
    <div v-if="chrome === 'bare' || isUnfurled" class="panel-body">
      <!-- Loading -->
      <div v-if="loading" class="state-message">Loading…</div>

      <!-- Empty thread — the empty-state copy adapts to the consent context.
           When no consent yet, the "Send consent prompt" affordance lives
           in the composer area below the keyline (not duplicated here). -->
      <div v-else-if="thread.length === 0" class="state-message empty">
        <p>No messages yet</p>
      </div>

      <!-- Thread -->
      <div v-else class="thread" ref="threadEl">
        <template v-for="msg in thread" :key="msg.id">
          <!-- inbound -->
          <div v-if="msg.type === 'inbound'" class="bubble-row inbound">
            <div class="bubble inbound">
              <div v-if="msg.mediaUrls && msg.mediaUrls.length" class="bubble-media">
                <img
                  v-for="(media, idx) in msg.mediaUrls"
                  :key="idx"
                  :src="media.url"
                  alt="attachment"
                  class="bubble-media-image"
                >
              </div>
              <div v-if="msg.body" class="bubble-body">{{ msg.body }}</div>
              <div class="bubble-meta">{{ formatTimestamp(msg.at) }}</div>
            </div>
          </div>

          <!-- outbound -->
          <div v-else-if="msg.type === 'outbound'" class="bubble-row outbound">
            <div class="bubble outbound">
              <div v-if="msg.mediaUrls && msg.mediaUrls.length" class="bubble-media">
                <img
                  v-for="(media, idx) in msg.mediaUrls"
                  :key="idx"
                  :src="media.url"
                  alt="attachment"
                  class="bubble-media-image"
                >
              </div>
              <div v-if="msg.body" class="bubble-body">{{ msg.body }}</div>
              <div class="bubble-meta">
                <span v-if="msg.sentBy && msg.sentBy !== 'system'" class="bubble-sender">
                  {{ msg.sentBy }} ·
                </span>
                <span v-else-if="msg.sentBy === 'system'" class="bubble-sender">
                  system ·
                </span>
                {{ formatTimestamp(msg.at) }}
              </div>
            </div>
          </div>

          <!-- system note -->
          <div v-else class="system-row">
            <div :class="['system-note', `severity-${msg.severity || 'info'}`]">
              <span class="system-icon">{{ systemIcon(msg) }}</span>
              <span class="system-body">{{ msg.body }}</span>
              <span class="system-meta">· {{ formatTimestamp(msg.at) }}</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Composer -->
      <div class="composer-section">
        <!-- granted (or consent not required in this context): full composer -->
        <template v-if="!requiresConsent || consent.status === 'granted'">
          <input
            ref="fileInput"
            type="file"
            accept="image/*,application/pdf"
            style="display: none"
            @change="onFileSelected"
          >

          <!-- attachment preview (only when attached) -->
          <div v-if="attachment" class="attachment-preview">
            <img
              v-if="isImageAttachment"
              :src="attachmentPreviewUrl"
              alt="preview"
              class="attachment-thumbnail"
            >
            <span v-else class="attachment-icon">📄</span>
            <span class="attachment-filename" :title="attachment.name">
              {{ attachment.name }}
            </span>
            <button class="attachment-remove" @click="removeAttachment">×</button>
          </div>

          <div class="composer">
            <input
              v-model="composerText"
              type="text"
              class="composer-input"
              placeholder="Type a message…"
              maxlength="500"
              :disabled="sending"
              @keyup.enter="handleSend"
            >
            <button
              class="attach-button"
              :disabled="sending || !!attachment"
              :title="attachment ? 'Remove current attachment first' : 'Attach file (image or PDF)'"
              @click="$refs.fileInput.click()"
            >
              <v-icon size="20">mdi-paperclip</v-icon>
            </button>
            <button
              class="send-button"
              :disabled="!canSend || sending"
              @click="handleSend"
            >{{ sending ? '…' : 'Send' }}</button>
          </div>

          <div
            v-if="composerText.length >= 480"
            class="char-counter"
            :class="{ warning: charCounterWarning }"
          >
            {{ composerText.length }} / 500
          </div>

          <div v-if="composerError" class="composer-error">{{ composerError }}</div>
        </template>

        <!-- pending -->
        <template v-else-if="consent.status === 'pending'">
          <div class="composer-pending">
            <div class="composer-hint pending-hint">
              Waiting for customer to reply YES to enable texting.
            </div>
            <div class="resend-row">
              <button
                class="inline-button"
                @click="$emit('sendConsentPrompt')"
              >Resend consent prompt</button>
              <button
                v-if="showResendInEnglish"
                class="inline-button"
                @click="$emit('sendConsentPrompt', { language: 'en-US' })"
              >Resend in English</button>
            </div>
          </div>
        </template>

        <!-- declined -->
        <template v-else-if="consent.status === 'declined'">
          <div class="composer-hint declined">
            Customer declined SMS communication{{ statusAtFormatted ? ` on ${statusAtFormatted}` : '' }}.
          </div>
        </template>

        <!-- revoked -->
        <template v-else-if="consent.status === 'revoked'">
          <div class="composer-hint revoked">
            ⛔ Customer opted out (STOP){{ statusAtFormatted ? ` on ${statusAtFormatted}` : '' }}.
          </div>
        </template>

        <!-- unknown -->
        <template v-else>
          <div class="composer-hint unknown">
            <span>No consent record exists for this number.</span>
            <button class="inline-button" @click="$emit('sendConsentPrompt')">
              Send consent prompt
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>


<script>
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB, matches AABB SMS unit
const ALLOWED_MIME = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
];

export default {
  name: 'SmsThreadPanel',

  props: {
    // [{ id, type: 'inbound'|'outbound'|'system', body, at, sentBy?,
    //    severity?, icon?, acknowledged?, mediaUrls? }, …]
    thread: {
      type: Array,
      default: () => [],
    },
    // { status: 'pending'|'granted'|'declined'|'revoked'|'unknown',
    //   statusAt: Date|string|null }
    consent: {
      type: Object,
      default: () => ({ status: 'unknown', statusAt: null }),
    },
    loading: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    // Detected language of the parent record (e.g., voicemail). Used to
    // decide whether to surface "Resend in English" in the pending state.
    recordLanguage: { type: String, default: null },

    // Visual chrome around the thread.
    //
    //   'full' (default) — renders an outer "Text messaging" accordion
    //                      header with chevron, consent pill, and unread
    //                      badge; thread body is hidden until the user
    //                      clicks to unfurl. This is the original
    //                      behavior used by VoiceMailRecord.
    //
    //   'bare'           — skips the outer accordion entirely; the
    //                      thread body is always shown. Useful when the
    //                      parent already provides its own "unfurl"
    //                      affordance, so the user shouldn't have to
    //                      click twice to see the messages. Used by
    //                      ParticipantMessages in the Forms dashboard.
    //
    // 'full' is preserved as the default so existing callers
    // (VoiceMailRecord) are unaffected.
    chrome: {
      type: String,
      default: 'full',
      validator: (v) => ['full', 'bare'].includes(v),
    },

    // Whether the SMS-consent gate applies in this context.
    //
    //   true (default)  — composer is hidden unless consent.status is
    //                     'granted'; pending/declined/revoked/unknown
    //                     each render their own state-specific UI
    //                     (resend-prompt button, decline message,
    //                     etc.). The status pill appears in the
    //                     header. This is the voicemail behavior.
    //
    //   false           — composer always renders regardless of
    //                     consent.status; status pill is hidden; the
    //                     consent-state branches in the composer are
    //                     dead code. Used by ParticipantMessages in
    //                     the BBAS dashboard, where SMS consent was
    //                     obtained out of band (the electronicConsents
    //                     flow) and isn't represented as an
    //                     smsConsents record. The send path needs to
    //                     forward sendWithoutConsent: true to the
    //                     server-side endpoint to actually bypass the
    //                     server's consent gate too.
    requiresConsent: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['unfurl', 'send', 'sendConsentPrompt', 'acknowledge'],

  data() {
    return {
      isUnfurled: false,
      composerText: '',
      composerError: '',
      attachment: null,
      attachmentPreviewUrl: '',
    };
  },

  computed: {
    unreadCount() {
      return this.thread.filter(
        (m) => m.type === 'inbound' && !m.acknowledged
      ).length;
    },

    statusText() {
      const map = {
        pending: 'awaiting reply',
        granted: 'consent granted',
        declined: 'declined',
        revoked: 'opted out',
        unknown: 'no consent yet',
      };
      return map[this.consent.status] || '—';
    },

    statusAtFormatted() {
      if (!this.consent.statusAt) return '';
      const d = new Date(this.consent.statusAt);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    canSend() {
      return (
        (this.composerText.trim().length > 0 || !!this.attachment)
        && !this.composerError
      );
    },

    isImageAttachment() {
      return this.attachment && this.attachment.type.startsWith('image/');
    },

    charCounterWarning() {
      return this.composerText.length >= 480;
    },

    // Show "Resend in English" only when the original prompt was in a
    // non-English supported language. For unsupported languages (or no
    // detected language at all), regular "Resend" already falls back
    // to en-US, so the dedicated English button would be redundant.
    showResendInEnglish() {
      return ['es-MX', 'ru-RU'].includes(this.recordLanguage);
    },
  },

  methods: {
    toggleUnfurl() {
      this.isUnfurled = !this.isUnfurled;
      if (this.isUnfurled) {
        this.$emit('unfurl');
        if (this.unreadCount > 0) {
          this.$emit('acknowledge');
        }
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    scrollToBottom() {
      const el = this.$refs.threadEl;
      if (el) el.scrollTop = el.scrollHeight;
    },

    formatTimestamp(at) {
      if (!at) return '';
      const d = new Date(at);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      if (sameDay) {
        return d.toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit',
        });
      }
      return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    },

    // Visual indicator for a system note. Per-message msg.icon takes
    // precedence (legacy/synthesized rows can still provide one); for
    // server-persisted rows we derive a sensible default from severity.
    // Specific reasons can override severity defaults — e.g. consent
    // events use distinctive icons regardless of underlying severity.
    systemIcon(msg) {
      if (msg.icon) return msg.icon;
      switch (msg.reason) {
        case 'consent_granted':  return '✓';
        case 'consent_declined': return '✗';
        case 'consent_revoked':  return '⛔';
      }
      switch (msg.severity) {
        case 'error':   return '⚠';
        case 'warning': return '⚠';
        default:        return 'ℹ';
      }
    },

    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      // Reset so picking the same file again still fires change
      event.target.value = '';
      if (!file) return;

      if (file.size > MAX_FILE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        this.composerError = `File too large (${sizeMB} MB). Max 5 MB.`;
        return;
      }
      if (!ALLOWED_MIME.includes(file.type)) {
        this.composerError = 'Only images and PDFs are allowed.';
        return;
      }

      this.composerError = '';
      this.attachment = file;
      this.attachmentPreviewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : '';
    },

    removeAttachment() {
      if (this.attachmentPreviewUrl) {
        URL.revokeObjectURL(this.attachmentPreviewUrl);
      }
      this.attachment = null;
      this.attachmentPreviewUrl = '';
      this.composerError = '';
    },

    handleSend() {
      if (!this.canSend || this.sending) return;
      this.$emit('send', {
        body: this.composerText,
        attachment: this.attachment,
      });
    },

    // Public method called by parent after successful send to clear UI.
    clearComposer() {
      this.composerText = '';
      this.removeAttachment();
    },
  },

  watch: {
    // Auto-scroll when new messages arrive (e.g., SSE push or after send).
    // Body is visible when chrome='bare' (always shown) or when
    // chrome='full' and the agent has unfurled the accordion.
    'thread.length'(newLen, oldLen) {
      const bodyVisible = this.chrome === 'bare' || this.isUnfurled;
      if (bodyVisible && newLen > oldLen) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
  },

  mounted() {
    // When the panel mounts already-open (chrome='bare'), scroll to the
    // most recent message. For chrome='full', scrolling happens on the
    // user's unfurl click.
    if (this.chrome === 'bare') {
      this.$nextTick(() => this.scrollToBottom());
    }
  },

  beforeUnmount() {
    if (this.attachmentPreviewUrl) {
      URL.revokeObjectURL(this.attachmentPreviewUrl);
    }
  },
};
</script>


<style scoped>
.sms-thread-panel {
  margin: 8px 0;
  border: 1px solid #d2b48c;
  border-radius: 6px;
  background-color: #f9f6ee;
  overflow: hidden;
}

/* ---- Header ---- */

.panel-header {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  background-color: #f5f5dc;
  border-bottom: 1px solid #e0d4b8;
  user-select: none;
}

.panel-header:hover {
  background-color: #f0eed1;
}

.chevron {
  font-size: 18px;
  color: #8b4513;
  margin-right: 8px;
  width: 12px;
  display: inline-block;
  text-align: center;
}

.label {
  flex: 1;
  font-size: 13px;
  color: #5c4633;
  font-weight: 500;
}

.status-pill {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: #ede4cc;
  color: #5c4633;
  margin-right: 6px;
}

.status-pill.status-granted {
  background-color: #d4e7d4;
  color: #2d5e2d;
}

.status-pill.status-pending {
  background-color: #f0e4c4;
  color: #6b5612;
}

.status-pill.status-declined,
.status-pill.status-revoked {
  background-color: #e8d4d4;
  color: #6b2d2d;
}

.unread-badge {
  background-color: #cc0000;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
}

/* ---- Body ---- */

.panel-body {
  display: flex;
  flex-direction: column;
  max-height: 300px;
}

.thread {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  max-height: 220px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.state-message {
  padding: 20px;
  text-align: center;
  color: #888;
  font-size: 13px;
}

.state-message.empty p {
  margin: 0 0 12px 0;
}

/* ---- Bubbles ---- */

.bubble-row {
  display: flex;
}

.bubble-row.inbound { justify-content: flex-start; }
.bubble-row.outbound { justify-content: flex-end; }

.bubble {
  max-width: 75%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
}

.bubble.inbound {
  background-color: white;
  border: 1px solid #d2b48c;
  color: #333;
}

.bubble.outbound {
  background-color: #ede4cc;
  color: #333;
}

.bubble-body {
  white-space: pre-wrap;
}

.bubble-meta {
  font-size: 10px;
  color: #888;
  margin-top: 4px;
}

.bubble-sender {
  color: #6b5612;
}

.bubble-media {
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bubble-media-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  object-fit: contain;
}

/* ---- System notes ---- */

.system-row {
  display: flex;
  justify-content: center;
  margin: 4px 0;
}

.system-note {
  font-size: 11px;
  font-style: italic;
  color: #888;
  padding: 2px 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.system-note.severity-warning { color: #b87a00; }
.system-note.severity-error { color: #b00020; }

.system-icon { font-style: normal; }
.system-meta { font-style: normal; color: #aaa; }

/* ---- Composer ---- */

.composer-section {
  border-top: 1px solid #e0d4b8;
  background-color: #faf8f0;
  padding: 8px 10px;
}

.attachment-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: white;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  margin-bottom: 6px;
  font-size: 12px;
}

.attachment-thumbnail {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 3px;
}

.attachment-icon {
  font-size: 24px;
  width: 32px;
  text-align: center;
}

.attachment-filename {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #5c4633;
}

.attachment-remove {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;
  line-height: 1;
}

.attachment-remove:hover { color: #b00020; }

.composer {
  display: flex;
  align-items: center;
  gap: 6px;
}

.composer-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  background-color: white;
}

.composer-input:focus { border-color: #8b4513; }

.attach-button,
.send-button {
  padding: 6px 12px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  background-color: #f5f5dc;
  cursor: pointer;
  font-size: 13px;
  color: #5c4633;
}

.attach-button {
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.attach-button .v-icon {
  color: #5c4633;
}

.attach-button:hover:not(:disabled),
.send-button:hover:not(:disabled) {
  background-color: #ede4cc;
}

.attach-button:disabled,
.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.send-button {
  font-weight: bold;
  background-color: #8b4513;
  color: white;
  border-color: #8b4513;
}

.send-button:hover:not(:disabled) {
  background-color: #a0561e;
}

.primary-button {
  padding: 6px 14px;
  border: 1px solid #8b4513;
  border-radius: 4px;
  background-color: #8b4513;
  color: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
}

.primary-button:hover { background-color: #a0561e; }

.char-counter {
  text-align: right;
  font-size: 11px;
  color: #888;
  margin-top: 4px;
}

.char-counter.warning {
  color: #b87a00;
  font-weight: bold;
}

.composer-error {
  margin-top: 4px;
  font-size: 12px;
  color: #b00020;
}

.composer-hint {
  font-size: 12px;
  color: #888;
  text-align: center;
  padding: 8px;
  font-style: italic;
}

.composer-pending {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

.composer-hint.pending-hint {
  padding: 4px 8px;
}

.resend-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}

.composer-hint.declined,
.composer-hint.revoked {
  color: #b00020;
}

.composer-hint.unknown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-style: normal;
}

.inline-button {
  background: none;
  border: 1px solid #8b4513;
  color: #8b4513;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.inline-button:hover {
  background-color: #8b4513;
  color: white;
}
</style>
